import React, { useEffect, useState } from "react";
import ExamService from "../../service/exam.service";
import PageNavigation from "../components/PageNavigation";
import { NotificationManager } from "react-notifications";
import { Calculator } from "lucide-react";

const RECALC_TYPES = {
    SCHOOL: "school",
    STUDENT: "student",
    SCHOOL_BRANCH: "school_branch",
    SCHOOL_GRADE: "school_grade",
};

export default function ResultRecalculate() {
    const [examList, setExamList] = useState([]);
    const [selectedExamId, setSelectedExamId] = useState("");
    const [recalcType, setRecalcType] = useState(RECALC_TYPES.SCHOOL);
    const [schools, setSchools] = useState([]);
    const [grades, setGrades] = useState([]);
    const [branches, setBranches] = useState([]);
    const [students, setStudents] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(false);
    const [loadingGrades, setLoadingGrades] = useState(false);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        schoolId: "",
        grade: "",
        branchId: "",
        studentId: "",
    });

    useEffect(() => {
        ExamService.getExamsWithAssignments()
            .then((response) => {
                if (response) setExamList(response);
            })
            .catch(() => setExamList([]));
    }, []);

    useEffect(() => {
        if (!selectedExamId) {
            setSchools([]);
            setGrades([]);
            setBranches([]);
            setStudents([]);
            setForm({ schoolId: "", grade: "", branchId: "", studentId: "" });
            return;
        }
        setLoadingSchools(true);
        ExamService.getExamFilterSchools(selectedExamId)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setSchools(response);
                } else {
                    setSchools([]);
                }
            })
            .catch(() => setSchools([]))
            .finally(() => setLoadingSchools(false));
        setForm({ schoolId: "", grade: "", branchId: "", studentId: "" });
        setGrades([]);
        setBranches([]);
        setStudents([]);
    }, [selectedExamId]);

    const needsGrades = [RECALC_TYPES.SCHOOL_GRADE, RECALC_TYPES.SCHOOL_BRANCH, RECALC_TYPES.STUDENT].includes(recalcType);
    const needsBranches = [RECALC_TYPES.SCHOOL_BRANCH, RECALC_TYPES.STUDENT].includes(recalcType);
    const needsStudents = recalcType === RECALC_TYPES.STUDENT;

    useEffect(() => {
        if (!needsGrades || !selectedExamId || !form.schoolId) {
            setGrades([]);
            return;
        }
        setLoadingGrades(true);
        ExamService.getExamFilterGrades(selectedExamId, form.schoolId)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setGrades(response);
                } else {
                    setGrades([]);
                }
            })
            .catch(() => setGrades([]))
            .finally(() => setLoadingGrades(false));
    }, [selectedExamId, form.schoolId, needsGrades]);

    useEffect(() => {
        if (!needsBranches || !selectedExamId || !form.schoolId || !form.grade) {
            setBranches([]);
            return;
        }
        setLoadingBranches(true);
        ExamService.getExamFilterBranches(selectedExamId, form.schoolId, form.grade)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setBranches(response);
                } else {
                    setBranches([]);
                }
            })
            .catch(() => setBranches([]))
            .finally(() => setLoadingBranches(false));
    }, [selectedExamId, form.schoolId, form.grade, needsBranches]);

    useEffect(() => {
        if (!needsStudents || !selectedExamId || !form.schoolId || !form.grade || !form.branchId) {
            setStudents([]);
            return;
        }
        setLoadingStudents(true);
        ExamService.getExamFilterStudents(selectedExamId, form.schoolId, form.grade, form.branchId)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setStudents(response);
                } else {
                    setStudents([]);
                }
            })
            .catch(() => setStudents([]))
            .finally(() => setLoadingStudents(false));
    }, [selectedExamId, form.schoolId, form.grade, form.branchId, needsStudents]);

    useEffect(() => {
        setForm((f) => ({ ...f, grade: "", branchId: "", studentId: "" }));
        setGrades([]);
        setBranches([]);
        setStudents([]);
    }, [recalcType]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedExamId) {
            NotificationManager.warning("Lütfen sınav seçin.", "Uyarı", 3000);
            return;
        }

        setSubmitting(true);
        try {
            if (recalcType === RECALC_TYPES.SCHOOL) {
                if (!form.schoolId) {
                    NotificationManager.warning("Lütfen okul seçin.", "Uyarı", 3000);
                    setSubmitting(false);
                    return;
                }
                const data = await ExamService.recalculateBySchool(selectedExamId, form.schoolId);
                if (data != null) {
                    const count = Array.isArray(data) ? data.length : 0;
                    NotificationManager.success(
                        `Okul için ${count} sonuç yeniden hesaplandı.`,
                        "Başarılı",
                        3000
                    );
                }
            } else if (recalcType === RECALC_TYPES.STUDENT) {
                if (!form.studentId?.trim()) {
                    NotificationManager.warning("Lütfen öğrenci seçin.", "Uyarı", 3000);
                    setSubmitting(false);
                    return;
                }
                const data = await ExamService.recalculateByStudent(selectedExamId, form.studentId.trim());
                if (data != null) {
                    NotificationManager.success("Öğrenci sonucu yeniden hesaplandı.", "Başarılı", 3000);
                } else {
                    NotificationManager.warning("Öğrenci bulunamadı veya sınava başlamamış.", "Uyarı", 3000);
                }
            } else if (recalcType === RECALC_TYPES.SCHOOL_BRANCH) {
                if (!form.schoolId || !form.branchId) {
                    NotificationManager.warning("Lütfen okul ve şube seçin.", "Uyarı", 3000);
                    setSubmitting(false);
                    return;
                }
                const data = await ExamService.recalculateBySchoolAndBranch(
                    selectedExamId,
                    form.schoolId,
                    form.branchId
                );
                if (data != null) {
                    const count = Array.isArray(data) ? data.length : 0;
                    NotificationManager.success(
                        `Okul + şube için ${count} sonuç yeniden hesaplandı.`,
                        "Başarılı",
                        3000
                    );
                }
            } else if (recalcType === RECALC_TYPES.SCHOOL_GRADE) {
                if (!form.schoolId || !form.grade) {
                    NotificationManager.warning("Lütfen okul ve sınıf seçin.", "Uyarı", 3000);
                    setSubmitting(false);
                    return;
                }
                const data = await ExamService.recalculateBySchoolAndGrade(
                    selectedExamId,
                    form.schoolId,
                    form.grade
                );
                if (data != null) {
                    const count = Array.isArray(data) ? data.length : 0;
                    NotificationManager.success(
                        `Okul + sınıf için ${count} sonuç yeniden hesaplandı.`,
                        "Başarılı",
                        3000
                    );
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <PageNavigation
                main="Componen List"
                sub="Home"
                header="Sonuç Yeniden Hesaplama"
                description="Sınav sonuçlarını okul, öğrenci, şube veya sınıfa göre yeniden hesaplatın"
            />
            <div className="card card-body shadow border-0 table-wrapper table-responsive mt-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Calculator size={20} className="text-primary" />
                    <span className="text-muted small">
                        Hangi kapsamda yeniden hesaplama yapılacak? Seçenekleri doldurup &quot;Hesaplat&quot; butonuna tıklayın.
                    </span>
                </div>

                <form onSubmit={handleSubmit} className="row g-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label">Sınav</label>
                        <select
                            className="form-select"
                            value={selectedExamId}
                            onChange={(e) => setSelectedExamId(e.target.value)}
                            required
                        >
                            <option value="">Sınav seçin...</option>
                            {examList.map((exam) => (
                                <option key={exam.id} value={exam.id}>
                                    {exam.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label">Hesaplama türü</label>
                        <select
                            className="form-select"
                            value={recalcType}
                            onChange={(e) => setRecalcType(e.target.value)}
                        >
                            <option value={RECALC_TYPES.SCHOOL}>Okula göre (examId + schoolId)</option>
                            <option value={RECALC_TYPES.STUDENT}>Öğrenciye göre (examId + studentId)</option>
                            <option value={RECALC_TYPES.SCHOOL_BRANCH}>Okul + şube (examId + schoolId + branchId)</option>
                            <option value={RECALC_TYPES.SCHOOL_GRADE}>Okul + sınıf (examId + schoolId + grade)</option>
                        </select>
                    </div>

                    {(recalcType === RECALC_TYPES.SCHOOL ||
                        recalcType === RECALC_TYPES.SCHOOL_GRADE ||
                        recalcType === RECALC_TYPES.SCHOOL_BRANCH ||
                        recalcType === RECALC_TYPES.STUDENT) && (
                        <div className="col-12 col-md-6">
                            <label className="form-label">Okul</label>
                            <select
                                className="form-select"
                                value={form.schoolId}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        schoolId: e.target.value,
                                        grade: "",
                                        branchId: "",
                                        studentId: "",
                                    }))
                                }
                                disabled={loadingSchools}
                            >
                                <option value="">Okul seçin...</option>
                                {schools.map((s, i) => (
                                    <option key={`school-${i}-${s.schoolId}`} value={s.schoolId}>
                                        {s.school} (ID: {s.schoolId})
                                    </option>
                                ))}
                            </select>
                            {loadingSchools && <span className="small text-muted ms-1">Yükleniyor...</span>}
                        </div>
                    )}

                    {needsGrades && (
                        <div className="col-12 col-md-6">
                            <label className="form-label">Sınıf</label>
                            <select
                                className="form-select"
                                value={form.grade}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        grade: e.target.value,
                                        branchId: "",
                                        studentId: "",
                                    }))
                                }
                                disabled={!form.schoolId || loadingGrades}
                            >
                                <option value="">Sınıf seçin...</option>
                                {grades.map((g, i) => (
                                    <option key={`grade-${i}-${g}`} value={g}>
                                        {g}
                                    </option>
                                ))}
                            </select>
                            {loadingGrades && <span className="small text-muted ms-1">Yükleniyor...</span>}
                        </div>
                    )}

                    {needsBranches && (
                        <div className="col-12 col-md-6">
                            <label className="form-label">Şube</label>
                            <select
                                className="form-select"
                                value={form.branchId}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        branchId: e.target.value,
                                        studentId: "",
                                    }))
                                }
                                disabled={!form.schoolId || !form.grade || loadingBranches}
                            >
                                <option value="">Şube seçin...</option>
                                {branches.map((b, i) => (
                                    <option key={`branch-${i}-${b.branchId}`} value={b.branchId}>
                                        {b.branch}
                                    </option>
                                ))}
                            </select>
                            {loadingBranches && <span className="small text-muted ms-1">Yükleniyor...</span>}
                        </div>
                    )}

                    {needsStudents && (
                        <div className="col-12 col-md-6">
                            <label className="form-label">Öğrenci</label>
                            <select
                                className="form-select"
                                value={form.studentId}
                                onChange={(e) => setForm((f) => ({ ...f, studentId: e.target.value }))}
                                disabled={!form.schoolId || !form.grade || !form.branchId || loadingStudents}
                            >
                                <option value="">Öğrenci seçin...</option>
                                {students.map((s, i) => (
                                    <option key={`student-${i}-${s.studentId}`} value={s.studentId}>
                                        {s.studentName} {s.studentLastname} ({s.studentId})
                                    </option>
                                ))}
                            </select>
                            {loadingStudents && <span className="small text-muted ms-1">Yükleniyor...</span>}
                        </div>
                    )}

                    <div className="col-12">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting ? "Hesaplanıyor..." : "Hesaplat"}
                        </button>
                    </div>
                </form>
            </div>
        </React.Fragment>
    );
}
